import {
  PROFESSIONAL_TOPPING_NAME,
  ServiceDurationOptionEnum,
} from "../constants/service.constants";
import { FormProps } from "../types/product-form.types";
import {
  CreateProductPayload,
  Product,
  ProductServiceSchedule,
  ProductType,
  Topping,
  ToppingValue,
  UpdateProductPayload,
} from "../types/products.types";

export const isService = (values: Pick<FormProps, "type">): boolean =>
  values.type === ProductType.Service;

export type ServiceUiState = {
  serviceDurationToggle: boolean;
  serviceDurationOption: ServiceDurationOptionEnum;
  serviceDurationHour: number;
  serviceDurationMinute: number;
  showScheduleInfo: boolean;
};

const DEFAULT_SERVICE_UI: ServiceUiState = {
  serviceDurationToggle: false,
  serviceDurationOption: ServiceDurationOptionEnum.None,
  serviceDurationHour: 0,
  serviceDurationMinute: 0,
  showScheduleInfo: true,
};

const getDurationOptionFromSchedule = (
  schedule?: ProductServiceSchedule
): ServiceDurationOptionEnum => {
  if (!schedule?.maxReservationHours && !schedule?.maxReservationMinutes) {
    return ServiceDurationOptionEnum.None;
  }
  const hours = schedule.maxReservationHours ?? 0;
  const minutes = schedule.maxReservationMinutes ?? 0;
  if (hours === 1 && minutes === 0) return ServiceDurationOptionEnum.One;
  if (hours === 2 && minutes === 0) return ServiceDurationOptionEnum.Two;
  if (hours === 3 && minutes === 0) return ServiceDurationOptionEnum.Three;
  return ServiceDurationOptionEnum.Other;
};

export const hydrateServiceUiFromProduct = (
  product: Partial<Product>
): ServiceUiState => {
  const schedule = product.schedule;
  const hasDuration =
    schedule?.maxReservationHours != null ||
    schedule?.maxReservationMinutes != null;
  const option = getDurationOptionFromSchedule(schedule);

  let hour = schedule?.maxReservationHours ?? 0;
  let minute = schedule?.maxReservationMinutes ?? 0;

  if (hasDuration && hour === 0 && minute > 0) {
    hour = Math.floor(minute / 60);
    minute = minute % 60;
  }

  return {
    serviceDurationToggle: hasDuration,
    serviceDurationOption: hasDuration ? option : ServiceDurationOptionEnum.None,
    serviceDurationHour: hour,
    serviceDurationMinute: minute,
    showScheduleInfo: true,
  };
};

export const buildScheduleFromUi = (
  values: FormProps
): ProductServiceSchedule => {
  if (!values.serviceDurationToggle) {
    return {
      activeHolidays: true,
      scheduleDaysConfiguration: [],
    };
  }

  const option = values.serviceDurationOption ?? ServiceDurationOptionEnum.None;

  if (option === ServiceDurationOptionEnum.One) {
    return {
      maxReservationHours: 1,
      maxReservationMinutes: 0,
      activeHolidays: scheduleActiveHolidays(values),
      scheduleDaysConfiguration: values.schedule?.scheduleDaysConfiguration ?? [],
    };
  }
  if (option === ServiceDurationOptionEnum.Two) {
    return {
      maxReservationHours: 2,
      maxReservationMinutes: 0,
      activeHolidays: scheduleActiveHolidays(values),
      scheduleDaysConfiguration: values.schedule?.scheduleDaysConfiguration ?? [],
    };
  }
  if (option === ServiceDurationOptionEnum.Three) {
    return {
      maxReservationHours: 3,
      maxReservationMinutes: 0,
      activeHolidays: scheduleActiveHolidays(values),
      scheduleDaysConfiguration: values.schedule?.scheduleDaysConfiguration ?? [],
    };
  }
  if (option === ServiceDurationOptionEnum.Other) {
    const hour = values.serviceDurationHour ?? 0;
    const minute = values.serviceDurationMinute ?? 0;
    if (minute === 0) {
      return {
        maxReservationHours: hour,
        maxReservationMinutes: 0,
        activeHolidays: scheduleActiveHolidays(values),
        scheduleDaysConfiguration:
          values.schedule?.scheduleDaysConfiguration ?? [],
      };
    }
    return {
      maxReservationHours: 0,
      maxReservationMinutes: hour * 60 + minute,
      activeHolidays: scheduleActiveHolidays(values),
      scheduleDaysConfiguration:
        values.schedule?.scheduleDaysConfiguration ?? [],
    };
  }

  return {
    activeHolidays: true,
    scheduleDaysConfiguration: [],
  };
};

const scheduleActiveHolidays = (values: FormProps): boolean =>
  values.schedule?.activeHolidays ?? true;

export const getProfessionalsFromToppings = (
  toppings?: Topping[]
): ToppingValue[] => {
  const professionalTopping = toppings?.find(
    (t) => t.name === PROFESSIONAL_TOPPING_NAME
  );
  if (!professionalTopping?.values?.length) {
    return [];
  }
  return professionalTopping.values;
};

export const buildProfessionalsTopping = (
  professionals: Array<{ value: string; enabled: boolean }>
): Topping[] => {
  const enabled = professionals.filter((p) => p.value.trim().length > 0);
  if (!enabled.length) {
    return [];
  }
  return [
    {
      id: "",
      name: PROFESSIONAL_TOPPING_NAME,
      isRequired: false,
      values: enabled.map((p) => ({
        value: p.value.trim(),
        name: "",
        additionalPrice: 0,
        enabled: p.enabled,
      })),
    },
  ];
};

const stripFormUiFields = (
  values: FormProps
): Omit<
  FormProps,
  | "imagesForm"
  | "imagesFiles"
  | "serviceDurationToggle"
  | "serviceDurationOption"
  | "serviceDurationHour"
  | "serviceDurationMinute"
  | "showScheduleInfo"
> => {
  const {
    imagesForm: _imagesForm,
    imagesFiles: _imagesFiles,
    serviceDurationToggle: _toggle,
    serviceDurationOption: _option,
    serviceDurationHour: _hour,
    serviceDurationMinute: _minute,
    showScheduleInfo: _info,
    ...rest
  } = values;
  return rest;
};

export const buildServicePayload = (
  values: FormProps,
  overrides: {
    category: string | null;
    images: string[];
    imagesDetail: { url: string }[];
  }
): CreateProductPayload | UpdateProductPayload => {
  const base = stripFormUiFields(values);
  const professionals = getProfessionalsFromToppings(values.toppings);
  const toppings = buildProfessionalsTopping(
    professionals.map((p) => ({ value: p.value, enabled: p.enabled }))
  );

  return {
    ...base,
    type: ProductType.Service,
    category: overrides.category,
    images: overrides.images,
    imagesDetail: overrides.imagesDetail,
    schedule: buildScheduleFromUi(values),
    toppings,
    inventory: undefined,
    wholesale: undefined,
    productToppingVariations: undefined,
    videoLink: undefined,
  };
};

export const buildProductPayload = (
  values: FormProps,
  overrides: {
    category: string | null;
    images: string[];
    imagesDetail: { url: string }[];
  }
): CreateProductPayload | UpdateProductPayload => {
  const base = stripFormUiFields(values);

  return {
    ...base,
    type: values.type ?? ProductType.Product,
    category: overrides.category,
    images: overrides.images,
    imagesDetail: overrides.imagesDetail,
    schedule: undefined,
  };
};

export const getDefaultServiceUi = (): ServiceUiState => ({ ...DEFAULT_SERVICE_UI });

export const resetFieldsOnTypeChange = (
  type: ProductType
): Partial<FormProps> => {
  if (type === ProductType.Service) {
    return {
      type: ProductType.Service,
      inventory: undefined,
      wholesale: undefined,
      productToppingVariations: undefined,
      videoLink: undefined,
      sku: undefined,
      toppings: [],
      schedule: undefined,
      ...getDefaultServiceUi(),
    };
  }
  return {
    type: ProductType.Product,
    schedule: undefined,
    toppings: undefined,
    serviceDurationToggle: false,
    serviceDurationOption: ServiceDurationOptionEnum.None,
    serviceDurationHour: 0,
    serviceDurationMinute: 0,
    showScheduleInfo: false,
  };
};

export const buildFormInitialValues = (
  product: CreateProductPayload | UpdateProductPayload
): FormProps => {
  const serviceUi =
    product.type === ProductType.Service
      ? hydrateServiceUiFromProduct(product)
      : getDefaultServiceUi();

  return {
    ...product,
    ...serviceUi,
    imagesFiles: [],
    imagesForm: [...(product.images || [])].map((image, index) => ({
      id: index,
      type: "url" as const,
      image,
    })),
  };
};
